import { useMemo } from 'react';
import useUserPreferences from '@elementary/common-ui/hooks/useUserPreferences';
import CommonAppProviders from '@elementary/common-ui/providers/AppProviders';
import { THEMES_BY_LOOK } from '@elementary/common-ui/style/looks';
import { useMediaQuery } from '@mui/material';
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RecoilRoot, useSetRecoilState } from 'recoil';
import useIsBoomiLookEnabled from 'shared/hooks/useIsBoomiLookEnabled';
import 'shared/services/i18n';
import * as atoms from 'shared/store/atoms';
import {
  SIGNATURE_EXPIRED_ERROR_CODE,
  getElementaryErrorCode,
  getPermissionErrorMessage,
} from 'shared/utils/permissionErrors';

const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

const QueryClientProviderWithPermissionHandling = ({
  children,
  disableCaching,
}: {
  children: React.ReactNode;
  disableCaching?: boolean;
}) => {
  const setSnackbar = useSetRecoilState(atoms.snackbar);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        mutationCache: new MutationCache({
          onError: (error) => {
            const errorMessage = getPermissionErrorMessage(error);
            if (errorMessage !== null) {
              setSnackbar((prev) => ({
                ...prev,
                open: true,
                variant: 'error',
                message: errorMessage,
                autoHide: false,
              }));
            }
            // Component-level onError handlers will still be called after this
          },
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              const elementaryErrorCode = getElementaryErrorCode(error);
              const isSignatureExpired =
                elementaryErrorCode === SIGNATURE_EXPIRED_ERROR_CODE;
              return (
                failureCount <= 3 &&
                (error as any).response?.status === 400 &&
                isSignatureExpired
              );
            },
            retryDelay: 3000,
            staleTime: disableCaching ? 0 : Infinity,
          },
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableCaching],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} position="bottom-left" />
    </QueryClientProvider>
  );
};

const ThemeProviders = ({ children }: { children: React.ReactNode }) => {
  const isBoomiLookEnabled = useIsBoomiLookEnabled();
  const { userPreferences } = useUserPreferences();
  const prefersDark = useMediaQuery(PREFERS_DARK_QUERY, { noSsr: true });
  const { appearance } = userPreferences;
  const isDark =
    appearance === 'dark' || (appearance === 'system' && prefersDark);
  const boomiLook = isDark ? 'boomi-dark' : 'boomi-light';

  return (
    <CommonAppProviders
      theme={THEMES_BY_LOOK[isBoomiLookEnabled ? boomiLook : 'elementary']}
    >
      {children}
    </CommonAppProviders>
  );
};

const AppProviders = ({
  children,
  disableCaching,
}: {
  children: React.ReactNode;
  disableCaching?: boolean;
}) => (
  <RecoilRoot>
    <QueryClientProviderWithPermissionHandling disableCaching={disableCaching}>
      <ThemeProviders>{children}</ThemeProviders>
    </QueryClientProviderWithPermissionHandling>
  </RecoilRoot>
);

export default AppProviders;
